import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Modal,
  Typography,
  Input,
  message,
  Table,
} from "antd";
import { ExclamationCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { format } from "date-fns";
import PropTypes from "prop-types";
import { getFileExtension } from "../../utils/getFileExtension";
import pallete from "../../utils/pallete";
import { trainingCatalogService } from "../../services/training-catalog.service";
import {
  COST_SOURCE_LABELS,
  MODAL_MESSAGES,
  TRAINING_CATEGORY_LABELS,
} from "../../utils/trainingCatalog.constants";
import StatusTag from "../../components/StatusTag";
import { useGetDetailApi } from "../../hooks/useApi";
import { useTableHeight } from "../../hooks/useTableHeight";
import { Icon } from "@iconify/react";
import TrainingTypeTag from "../../components/TrainingTypeTag";
import { fileService } from "../../services/file.service";
import DocumentPreviewModal from "../../components/DocumentPreviewModal";

/**
 * Reusable Training Catalog Detail Component
 * Can be used for OnGoing, Accepted, and Rejected pages
 *
 * @param {Object} props
 * @param {string} props.type - Type of catalog: 'on-going', 'accepted', 'rejected'
 * @param {string} props.title - Page title
 * @param {boolean} props.showActions - Show verify/reject buttons
 * @param {boolean} props.showStatus - Show status section
 * @param {string} props.backRoute - Route to go back
 */
export default function TrainingCatalogDetail({
  type = "pending",
  title = "Detail Katalog",
  showActions = false,
  showStatus = false,
  backRoute = "/training-catalog/pending",
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const breakpoint = Grid.useBreakpoint();
  const queryClient = useQueryClient();

  const [messageApi, contextHolder] = message.useMessage();
  const [previewFileData, setPreviewFileData] = useState(null);

  const extractUUID = (filePath) => {
    if (!filePath) return null;
    const match = filePath.match(
      /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/,
    );
    return match ? match[0] : null;
  };

  const handlePreview = async (filePath, isPdf) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Memuat dokumen...", key: "preview" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      setPreviewFileData({ url, isPdf });
      messageApi.success({
        content: "Dokumen berhasil dimuat",
        key: "preview",
        duration: 2,
      });
    } catch (error) {
      console.error("Preview error:", error);
      messageApi.error({
        content: "Gagal memuat dokumen",
        key: "preview",
        duration: 2,
      });
    }
  };

  const [confirmVerify, setConfirmVerify] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const tableHeight = useTableHeight();
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response =
        await trainingCatalogService.exportAcceptedParticipantsExcel(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName =
        response.headers["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || `peserta-katalog-pelatihan-${id}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      messageApi.success("File berhasil diunduh");
    } catch (error) {
      console.error("Export excel error:", error);
      messageApi.error("Gagal mengunduh file Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const labelStyle = {
    width: breakpoint.lg ? 260 : "100%",
    minWidth: 260,
  };

  const contentStyle = {
    width: breakpoint.lg ? "calc(100% - 260px)" : "100%",
  };

  const descriptionTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 18,
      }}
    >
      {title}
    </Typography.Title>
  );

  const { data } = useGetDetailApi({
    id,
    serviceFn: trainingCatalogService.fetchById,
    status: type,
    queryKey: ["trainingCatalog"],
  });

  const { mutate: handleVerifyMutation, isPending: isVerifying } = useMutation({
    mutationFn: () => trainingCatalogService.verify(id),
    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.VERIFY.SUCCESS);
      setConfirmVerify(false);
      queryClient.invalidateQueries(["trainingCatalog"]);
      setTimeout(() => navigate(backRoute), 1500);
    },
    onError: (error) => {
      console.error("Verify error:", error);
      messageApi.error(MODAL_MESSAGES.VERIFY.ERROR);
    },
  });

  const { mutate: handleRejectMutation, isPending: isRejecting } = useMutation({
    mutationFn: (reason) => trainingCatalogService.reject(id, reason),
    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.REJECT.SUCCESS);
      setRejectReason("");
      setConfirmReject(false);
      queryClient.invalidateQueries(["trainingCatalog"]);
      setTimeout(() => navigate(backRoute), 1500);
    },
    onError: (error) => {
      console.error("Reject error:", error);
      messageApi.error(MODAL_MESSAGES.REJECT.ERROR);
    },
  });

  const handleVerify = () => {
    handleVerifyMutation();
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      messageApi.warning(MODAL_MESSAGES.REJECT.REASON_REQUIRED);
      return;
    }
    handleRejectMutation(rejectReason);
  };

  const statusItems = useMemo(() => {
    if (!data || !showStatus) return [];

    const items = [
      {
        label: "Status",
        children: <StatusTag status={data.status} />,
        span: 2,
      },
    ];

    if (data?.status === "Ditolak") {
      items.push({
        label: "Alasan",
        children: data?.rejectReason ?? "-",
        span: 2,
      });
    }

    return items;
  }, [data, showStatus, type]);

  const trainingDataItems = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: "Gambar Pelatihan",
        span: 2,
        children: (
          <Flex gap={8} align="center">
            <Typography.Text style={{ marginRight: "auto" }}>
              {data?.imageName || "-"}
            </Typography.Text>
            {data?.imagePath && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() =>
                  handlePreview(
                    data?.imagePath,
                    data?.imageName?.toLowerCase().includes(".pdf"),
                  )
                }
              >
                Lihat
              </Button>
            )}
          </Flex>
        ),
      },
      {
        label: "Judul Pelatihan",
        children: data?.title || "-",
        span: 2,
      },
      {
        label: "Jenis Pelatihan",
        children: <TrainingTypeTag type={data?.category} /> || "-",
        span: 2,
      },
      {
        label: "Sumber Biaya",
        children: COST_SOURCE_LABELS[data?.costSources] || "-",
      },
      {
        label: "Durasi Program",
        children: `${data?.numberOfHours} jam` || "0 jam",
      },
      {
        label: "Deskripsi Pelatihan",
        children: data?.description || "-",
        span: 2,
      },
      {
        label: "Tanggal Mulai",
        children: data?.startDate
          ? format(new Date(data?.startDate), "dd/MM/yyyy")
          : "-",
      },
      {
        label: "Tanggal Akhir",
        children: data?.endDate
          ? format(new Date(data?.endDate), "dd/MM/yyyy")
          : "-",
      },
      {
        label: "Jumlah Jam",
        children: `${data?.numberOfHours} jam` || "0 jam",
      },
      {
        label: "Kuota Pelatihan",
        children: data?.quota || "0",
      },
      {
        label: "Program Pelatihan",
        children: data?.program || "-",
        span: 2,
      },
      {
        label: "Negara Tujuan",
        children: data?.destinationCountry || "-",
        span: 2,
      },
      {
        label: "Tanggal Pengajuan",
        children: data?.submissionDate
          ? format(new Date(data?.submissionDate), "dd/MM/yyyy")
          : "-",
      },
      {
        label: "Diinput Oleh",
        children: data?.inputBy ?? "-",
      },
    ];
  }, [data, type]);

  const vocationalDataItems = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: "Nama Lembaga",
        children: data?.institution || "-",
        span: 2,
      },
      {
        label: "Alamat",
        children: data?.institutionAddress || "-",
        span: 2,
      },
      {
        label: "Email",
        children: data?.institutionEmail || "-",
      },
      {
        label: "Telepon",
        children: data?.institutionPhone || "-",
      },
    ];
  }, [data, type]);

  const participantColumns = [
    {
      title: "No",
      align: "center",
      dataIndex: "id",
      width: 50,
      fixed: breakpoint.md && "left",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Nama & Jenis Kelamin",
      width: 130,
      fixed: breakpoint.md && "left",
      render: (record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.name}</div>
          <div style={{ fontSize: 12, color: pallete.grey[800] }}>
            {record.gender}
          </div>
        </div>
      ),
    },
    {
      title: "No Telepon",
      dataIndex: "phone",
      width: 150,
    },
    {
      title: "Negeri Peminatan",
      dataIndex: "requestedCountry",
      width: 140,
    },
    {
      title: "Sektor Pekerjaan",
      dataIndex: "sectoral",
      width: 140,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (val) => <StatusTag status={val} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="default"
          variant="filled"
          onClick={() =>
            navigate(
              `/training-catalog/${type}/detail/${id}/participant/${record.uuid}`,
            )
          }
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Card title={descriptionTitle(title)}>
        {showStatus && data?.status == "Ditolak" && (
          <>
            <Descriptions
              style={{ marginBottom: 20 }}
              column={breakpoint.lg ? 2 : 1}
              bordered
              size="small"
              styles={{
                label: labelStyle,
                content: contentStyle,
              }}
              title={descriptionTitle("Status")}
              layout={breakpoint.lg ? "horizontal" : "vertical"}
              items={statusItems}
            />
          </>
        )}

        {type != "rejected" && (
          <Descriptions
            style={{ marginBottom: 20 }}
            column={breakpoint.lg ? 2 : 1}
            bordered
            size="small"
            styles={{
              label: labelStyle,
              content: contentStyle,
            }}
            title={descriptionTitle("Data Lembaga Vokasi")}
            layout={breakpoint.lg ? "horizontal" : "vertical"}
            items={vocationalDataItems}
          />
        )}

        <Descriptions
          style={{ marginBottom: 20 }}
          column={breakpoint.lg ? 2 : 1}
          bordered
          size="small"
          styles={{
            label: labelStyle,
            content: contentStyle,
          }}
          title={descriptionTitle("Data Katalog Pelatihan")}
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={trainingDataItems}
        />

        {showActions && (
          <Flex justify="end" align="center" style={{ marginTop: 24 }}>
            {/* TOMBOL KANAN */}
            <Flex gap={12}>
              <Button
                type="primary"
                loading={isVerifying}
                disabled={isRejecting}
                style={{
                  backgroundColor: pallete.primary[500],
                  fontWeight: 600,
                }}
                onClick={() => setConfirmVerify(true)}
              >
                Verifikasi
              </Button>

              <Button
                danger
                loading={isRejecting}
                disabled={isVerifying}
                style={{ fontWeight: 600 }}
                onClick={() => setConfirmReject(true)}
              >
                Tolak
              </Button>
            </Flex>
          </Flex>
        )}

        {showStatus && data?.status == "Diterima" && (
          <>
            <Descriptions
              style={{ marginBottom: 20 }}
              column={breakpoint.lg ? 2 : 1}
              bordered
              size="small"
              styles={{
                label: labelStyle,
                content: contentStyle,
              }}
              title={descriptionTitle("Status")}
              layout={breakpoint.lg ? "horizontal" : "vertical"}
              items={statusItems}
            />
            <Flex
              justify="space-between"
              align="center"
              style={{ marginTop: 20 }}
            >
              {descriptionTitle("Data Peserta Pelatihan")}
              <Button
                type="primary"
                icon={
                  <Icon
                    icon="solar:export-bold-duotone"
                    width={18}
                    height={18}
                  />
                }
                loading={exportLoading}
                onClick={handleExportExcel}
              >
                Export Excel Peserta
              </Button>
            </Flex>
            <Table
              style={{
                marginTop: 20,
              }}
              columns={participantColumns}
              pagination={false}
              size="small"
              rowKey={"id"}
              scroll={{ y: tableHeight, x: 1000 }}
              dataSource={data?.participants ?? []}
            />
          </>
        )}
      </Card>

      <DocumentPreviewModal
        open={!!previewFileData}
        src={previewFileData?.url || ""}
        isPdf={previewFileData?.isPdf}
        title={previewFileData?.isPdf ? "Preview Dokumen" : "Preview Gambar Pelatihan"}
        onClose={() => {
          if (previewFileData?.url) URL.revokeObjectURL(previewFileData.url);
          setPreviewFileData(null);
        }}
      />

      {showActions && (
        <>
          <Modal
            open={confirmVerify}
            centered
            footer={null}
            closable={false}
            width={400}
            onCancel={() => !isVerifying && setConfirmVerify(false)}
          >
            <Flex vertical align="center" justify="center" gap={12}>
              <div
                style={{
                  backgroundColor: "#FFF4E5",
                  borderRadius: "50%",
                  padding: 16,
                }}
              >
                <ExclamationCircleFilled
                  style={{ color: "#FAAD14", fontSize: 32 }}
                />
              </div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {MODAL_MESSAGES.VERIFY.TITLE}
              </Typography.Title>
              <Typography.Text style={{ textAlign: "center" }}>
                {MODAL_MESSAGES.VERIFY.DESCRIPTION}
              </Typography.Text>
              <Flex gap={8} style={{ marginTop: 16 }}>
                <Button
                  onClick={() => setConfirmVerify(false)}
                  disabled={isVerifying}
                >
                  Batal
                </Button>
                <Button
                  type="primary"
                  style={{ backgroundColor: pallete.primary[500] }}
                  onClick={handleVerify}
                  loading={isVerifying}
                >
                  Verifikasi
                </Button>
              </Flex>
            </Flex>
          </Modal>

          {/* Modal Konfirmasi Tolak */}
          <Modal
            open={confirmReject}
            centered
            footer={null}
            closable={false}
            width={450}
            onCancel={() => !isRejecting && setConfirmReject(false)}
          >
            <Flex vertical align="center" justify="center" gap={12}>
              <div
                style={{
                  backgroundColor: "#FFF1F0",
                  borderRadius: "50%",
                  padding: 16,
                }}
              >
                <CloseCircleFilled style={{ color: "#FF4D4F", fontSize: 32 }} />
              </div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {MODAL_MESSAGES.REJECT.TITLE}
              </Typography.Title>
              <Typography.Text style={{ textAlign: "center" }}>
                {MODAL_MESSAGES.REJECT.DESCRIPTION}
              </Typography.Text>
              <div style={{ width: "100%", marginTop: 12 }}>
                <Typography.Text strong>
                  {MODAL_MESSAGES.REJECT.REASON_LABEL}
                </Typography.Text>
                <Input.TextArea
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={MODAL_MESSAGES.REJECT.REASON_PLACEHOLDER}
                  disabled={isRejecting}
                  maxLength={500}
                  showCount
                />
              </div>
              <Flex gap={8} style={{ marginTop: 16 }}>
                <Button
                  onClick={() => {
                    setConfirmReject(false);
                    setRejectReason("");
                  }}
                  disabled={isRejecting}
                >
                  Batal
                </Button>
                <Button
                  danger
                  type="primary"
                  onClick={handleReject}
                  loading={isRejecting}
                >
                  Kirim
                </Button>
              </Flex>
            </Flex>
          </Modal>
        </>
      )}
    </>
  );
}

TrainingCatalogDetail.propTypes = {
  type: PropTypes.oneOf(["on-going", "accepted", "rejected"]).isRequired,
  title: PropTypes.string,
  showActions: PropTypes.bool,
  showStatus: PropTypes.bool,
  backRoute: PropTypes.string,
};
